'use client';

import React from 'react';

interface FooterProps {
  onScrollTo: (section: string) => void;
}

export default function Footer({ onScrollTo }: FooterProps) {
  return (
    <footer
      className="relative pt-16 pb-10 overflow-hidden"
      style={{ background: 'rgb(3, 3, 5)', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
      
      {/* Top gradient line */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(to right, transparent, rgba(59, 130, 246, 0.4), rgba(157, 76, 221, 0.4), transparent)' }} />
      

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="mb-4">
              <img
                alt="Scalio AI Agency"
                className="logo-img"
                src="/assets/images/ChatGPT_Image_Apr_5__2026__05_26_18_PM-1779730470887.png" />
              
            </div>
            <p className="text-sm text-neutral-500 leading-relaxed max-w-xs mb-5">
              We help brands grow using AI-powered content, automation, and scaling systems.
            </p>
            <p className="text-xs text-neutral-600 tracking-[0.15em] uppercase font-medium">Elevate with AI</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-5">Quick Links</h4>
            <div className="space-y-3">
              {['Services', 'Results', 'Process', 'About'].map((item) =>
              <button
                key={item}
                onClick={() => onScrollTo(item.toLowerCase())}
                className="block text-sm text-neutral-400 hover:text-white transition-colors duration-200 text-left">
                
                  {item}
                </button>
              )}
              <a
                href="https://calendly.com/creatormaska/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200">
                
                Book a Call →
              </a>
            </div>
          </div>

          {/* Get In Touch */}
          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-5">Get In Touch</h4>
            <div className="space-y-3">
              <a
                href="mailto:Creatormaska@gmail.com"
                className="flex items-center gap-2.5 text-sm text-neutral-400 hover:text-white transition-colors group">
                
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500 flex-shrink-0" aria-hidden="true">
                  <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                </svg>
                Creatormaska@gmail.com
              </a>
              <a
                href="https://wa.me/254790083937"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-neutral-400 hover:text-white transition-colors group">
                
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 flex-shrink-0" aria-hidden="true">
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                </svg>
                +254 790 083937
              </a>
              <a
                href="https://www.instagram.com/scalioai/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-neutral-400 hover:text-white transition-colors group">
                
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-500 flex-shrink-0" aria-hidden="true">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
                @scalioai
              </a>
              <a
                href="https://www.tiktok.com/@scalioai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-neutral-400 hover:text-white transition-colors group">
                
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-neutral-400 flex-shrink-0">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.27 8.27 0 0 0 4.83 1.54V6.96a4.84 4.84 0 0 1-1.06-.27z" />
                </svg>
                @scalioai
              </a>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.instagram.com/scalioai/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl flex items-center justify-center text-neutral-500 hover:text-white transition-colors"
                aria-label="Instagram"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.07)' }}>
                
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@scalioai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl flex items-center justify-center text-neutral-500 hover:text-white transition-colors"
                aria-label="TikTok"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.07)' }}>
                
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.27 8.27 0 0 0 4.83 1.54V6.96a4.84 4.84 0 0 1-1.06-.27z" />
                </svg>
              </a>
              <a
                href="https://wa.me/254790083937"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl flex items-center justify-center text-neutral-500 hover:text-white transition-colors"
                aria-label="WhatsApp"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.07)' }}>
                
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.04)' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-neutral-700">© 2026 Scalio AI Agency. All rights reserved.</p>
            <a
              href="https://calendly.com/creatormaska/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-button inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold text-white">
              
              Book a Call
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>);

}